import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download, Check, Loader2, ArrowRight } from "lucide-react";

export default function DownloadDialog({
  open,
  onClose,
  onSubmit,
  unlocked,
  onDownloadAll,
}) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!valid) {
      setError("Enter a valid email address.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await onSubmit(email);
    } catch (err) {
      setError("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="download-dialog"
          className="fixed inset-0 z-[90] flex items-center justify-center p-5"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg bg-black border border-zinc-800 p-7 md:p-10"
          >
            <button
              data-testid="dialog-close"
              aria-label="Close"
              onClick={onClose}
              className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors"
            >
              <X size={20} />
            </button>

            {!unlocked ? (
              <>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
                  Free Download
                </p>
                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mb-4">
                  Take the tape
                  <br />
                  with you.
                </h3>
                <p className="font-body text-sm text-zinc-400 mb-8 max-w-sm">
                  Drop your email once and unlock the entire FRE3BRD mixtape —
                  all ten tracks, free to download.
                </p>

                <form onSubmit={submit}>
                  <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                    Email Address
                  </label>
                  <input
                    data-testid="email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    autoFocus
                    className="w-full bg-transparent border-b border-zinc-700 text-white text-xl md:text-2xl font-display py-3 focus:border-white focus:outline-none placeholder:text-zinc-700 transition-colors"
                  />
                  {error && (
                    <p
                      data-testid="email-error"
                      className="font-mono text-[11px] text-red-400 mt-3"
                    >
                      {error}
                    </p>
                  )}
                  <button
                    data-testid="email-submit"
                    type="submit"
                    disabled={loading}
                    className="mt-8 w-full bg-white text-black py-4 font-mono uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors flex items-center justify-center gap-3 disabled:opacity-70"
                  >
                    {loading ? (
                      <Loader2 size={15} className="animate-spin" />
                    ) : (
                      <ArrowRight size={15} />
                    )}
                    {loading ? "Unlocking" : "Unlock Download"}
                  </button>
                </form>
              </>
            ) : (
              <div data-testid="dialog-unlocked">
                <div className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center mb-5">
                  <Check size={22} className="text-white" />
                </div>
                <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mb-4">
                  You're in.
                </h3>
                <p className="font-body text-sm text-zinc-400 mb-8 max-w-sm">
                  Download is unlocked. Grab the full tape below, or hit the
                  download icon on any track.
                </p>
                <button
                  data-testid="dialog-download-all"
                  onClick={onDownloadAll}
                  className="w-full bg-white text-black py-4 font-mono uppercase tracking-widest text-xs hover:bg-zinc-200 transition-colors flex items-center justify-center gap-3"
                >
                  <Download size={15} />
                  Download Full Tape
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
